import React, { useEffect } from 'react';
import {
    Popover,
    Button,
    Divider,
} from 'antd';
import { RightOutlined } from '@ant-design/icons';

const RowContextMenuItems = ({
    items = [],
    closeMenu,
    getPopupContainer,
}) => {
    const visibleItems = items.filter(
        (item) =>
            item != null &&
            item.visible !== false,
    );

    return (
        <div
            style={{
                minWidth: 160,
                display: 'flex',
                flexDirection: 'column',
            }}
            onContextMenu={(event) => {
                event.preventDefault();
            }}
        >
            {visibleItems.map((item, index) => {
                if (item.divider) {
                    return (
                        <Divider
                            key={
                                item.key ??
                                `divider-${index}`
                            }
                            style={{
                                margin: '4px 0',
                            }}
                        />
                    );
                }

                if (
                    typeof item.render ===
                    'function'
                ) {
                    return (
                        <div
                            key={item.key}
                            style={{
                                padding: '2px 0',
                            }}
                        >
                            {item.render(closeMenu)}
                        </div>
                    );
                }

                const childItems = (
                    item.children ?? []
                ).filter(
                    (child) =>
                        child != null &&
                        child.visible !== false,
                );

                if (childItems.length > 0) {
                    return (
                        <Popover
                            key={item.key}
                            placement="rightTop"
                            trigger="hover"
                            destroyTooltipOnHide
                            mouseEnterDelay={0.05}
                            mouseLeaveDelay={0.15}
                            getPopupContainer={
                                getPopupContainer
                            }
                            content={
                                <RowContextMenuItems
                                    items={childItems}
                                    closeMenu={
                                        closeMenu
                                    }
                                    getPopupContainer={
                                        getPopupContainer
                                    }
                                />
                            }
                        >
                            <Button
                                type="text"
                                icon={item.icon}
                                disabled={
                                    item.disabled
                                }
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems:
                                        'center',
                                    justifyContent:
                                        'space-between',
                                    textAlign: 'left',
                                }}
                            >
                                <span>
                                    {item.label}
                                </span>

                                <RightOutlined
                                    style={{
                                        marginLeft: 12,
                                        fontSize: 10,
                                    }}
                                />
                            </Button>
                        </Popover>
                    );
                }

                return (
                    <Button
                        key={item.key}
                        type="text"
                        icon={item.icon}
                        disabled={item.disabled}
                        danger={item.danger}
                        style={{
                            width: '100%',
                            textAlign: 'left',
                            justifyContent:
                                'flex-start',
                        }}
                        onClick={() => {
                            item.onClick?.(
                                closeMenu,
                            );

                            closeMenu();
                        }}
                    >
                        {item.label}
                    </Button>
                );
            })}
        </div>
    );
};

/**
 * Controlled right-click menu used by the tables.
 *
 * Item shape:
 * {
 *     key: string,
 *     label?: string,
 *     icon?: ReactNode,
 *     onClick?: (closeMenu) => void,
 *     render?: (closeMenu) => ReactNode,
 *     children?: Item[],
 *     visible?: boolean,
 *     divider?: boolean,
 *     disabled?: boolean,
 *     danger?: boolean,
 * }
 */
const RowContextMenu = ({
    open,
    onOpenChange,
    items,
    title,
    placement = 'rightTop',
    children,
    getPopupContainer,
}) => {
    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            const insidePopover =
                event.target.closest?.(
                    '.ant-popover',
                );

            if (!insidePopover) {
                onOpenChange?.(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onOpenChange?.(false);
            }
        };

        document.addEventListener(
            'pointerdown',
            handlePointerDown,
            true,
        );

        document.addEventListener(
            'keydown',
            handleKeyDown,
        );

        return () => {
            document.removeEventListener(
                'pointerdown',
                handlePointerDown,
                true,
            );

            document.removeEventListener(
                'keydown',
                handleKeyDown,
            );
        };
    }, [open, onOpenChange]);

    const closeMenu = () => {
        onOpenChange?.(false);
    };

    const content = (
        <RowContextMenuItems
            items={items}
            closeMenu={closeMenu}
            getPopupContainer={
                getPopupContainer
            }
        />
    );

    return (
        <Popover
            visible={open}
            onVisibleChange={onOpenChange}
            content={content}
            title={title}
            placement={placement}
            trigger={[]}
            destroyTooltipOnHide
            getPopupContainer={
                getPopupContainer
            }
        >
            {children}
        </Popover>
    );
};

export default RowContextMenu;